import { useEffect, useState, useMemo, useRef, useId } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Check,
  Trash2,
  ShieldCheck,
  Lock,
  FileCheck,
  KeyRound,
  Gift,
  ChevronDown,
  Image as ImageIcon,
  Receipt,
  AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { BRAND } from "@/brand";
import { toast } from "sonner";

const PAYOUT_METHODS = [
  { id: "paypal", name: "PayPal", label: "PayPal Email Address", placeholder: "you@example.com", hint: "e.g., john.doe@gmail.com" },
  { id: "zelle", name: "Zelle", label: "Zelle Email or Phone", placeholder: "you@example.com or +1...", hint: "Must be linked to a US bank account" },
  { id: "cashapp", name: "Cash App", label: "$Cashtag", placeholder: "$yourtag", hint: "Starts with $" },
  { id: "bitcoin", name: "Bitcoin", label: "BTC Wallet Address", placeholder: "bc1q...", hint: "Network fees may apply" },
  { id: "chime", name: "Chime", label: "Chime $ChimeSign or Email", placeholder: "$ChimeSign or email", hint: "Tied to your Chime account" },
];

const EASE = [0.2, 0.7, 0.2, 1];

const blankCard = () => ({
  _id: crypto.randomUUID(),
  brand_id: "",
  brand_query: "",
  balance: "",
  card_number: "",
  pin: "",
  expiry_date: "",
  card_type: "physical",
  condition: "unused",
  has_receipt: false,
  image_front: "",
  image_back: "",
  image_receipt: "",
});

export default function SubmitPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();

  const [brands, setBrands] = useState([]);
  const [cfg, setCfg] = useState({ min_per_card: 50, welcome_bonus_amount: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [touched, setTouched] = useState({});
  const [attempted, setAttempted] = useState(false);

  const markTouched = (k) => setTouched((t) => ({ ...t, [k]: true }));
  const showErr = (k) => (touched[k] || attempted);

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    cards: [blankCard()],
    payout_method: "paypal",
    payout_handle: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/brands?limit=200").then((r) => {
      const data = r.data || [];
      setBrands(data);
      const slug = params.get("brand");
      const balanceParam = params.get("balance");
      if (slug) {
        const b = data.find((x) => x.slug === slug);
        if (b) {
          setForm((f) => {
            const cards = [...f.cards];
            cards[0] = { ...cards[0], brand_id: b.id, brand_query: b.name, balance: balanceParam || cards[0].balance };
            return { ...f, cards };
          });
        }
      } else if (balanceParam) {
        setForm((f) => {
          const cards = [...f.cards];
          cards[0] = { ...cards[0], balance: balanceParam };
          return { ...f, cards };
        });
      }
    });
    api.get("/config").then((r) => setCfg(r.data || {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCard = (idx, patch) =>
    setForm((f) => {
      const cards = [...f.cards];
      cards[idx] = { ...cards[idx], ...patch };
      return { ...f, cards };
    });

  const addCard = () => setForm((f) => ({ ...f, cards: [...f.cards, blankCard()] }));

  const removeCard = (idx) =>
    setForm((f) => ({ ...f, cards: f.cards.length > 1 ? f.cards.filter((_, i) => i !== idx) : f.cards }));

  // Live payout preview
  const preview = useMemo(() => {
    let total = 0;
    const lines = [];
    for (const c of form.cards) {
      const brand = brands.find((b) => b.id === c.brand_id);
      const bal = Number(c.balance) || 0;
      if (!brand || !bal) {
        lines.push({ name: c.brand_query || "—", amount: 0, rate: 0 });
        continue;
      }
      const rates = brand.rates || { receipt: brand.rate, no_receipt: brand.rate, partial: brand.rate };
      let rate;
      if (c.condition === "partial") rate = rates.partial;
      else if (c.has_receipt) rate = rates.receipt;
      else rate = rates.no_receipt;
      const amount = Math.round(bal * (rate / 100.0) * 100) / 100;
      total += amount;
      lines.push({ name: brand.name, amount, rate, balance: bal });
    }
    return { lines, total: Math.round(total * 100) / 100 };
  }, [form.cards, brands]);

  const errors = useMemo(() => {
    const e = {};
    if (!form.full_name.trim() || form.full_name.trim().length < 2) e.full_name = "Enter your full name";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.phone.trim()) e.phone = "Enter a phone number";
    form.cards.forEach((c, i) => {
      const bal = Number(c.balance) || 0;
      if (!c.brand_query.trim()) e[`card-${i}-brand`] = "Enter a card brand";
      const minB = cfg.min_per_card || 50;
      const brand = brands.find((b) => b.id === c.brand_id);
      const maxB = brand?.max || 2000;
      if (bal < minB) e[`card-${i}-balance`] = `Minimum $${minB} per card`;
      else if (bal > maxB) e[`card-${i}-balance`] = `Maximum $${maxB} for this brand`;
    });
    if (!form.payout_handle.trim()) e.payout_handle = "Enter your payout handle";
    return e;
  }, [form, brands, cfg]);

  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const submit = async () => {
    setAttempted(true);
    if (!canSubmit) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        cards: form.cards.map((c) => ({
          brand_id: c.brand_id,
          brand_query: c.brand_query.trim(),
          balance: Number(c.balance),
          card_number: c.card_number,
          pin: c.pin,
          expiry_date: c.expiry_date,
          card_type: c.card_type,
          condition: c.condition,
          has_receipt: c.has_receipt,
          image_front: c.image_front,
          image_back: c.image_back,
          image_receipt: c.image_receipt,
        })),
        payout_method: form.payout_method,
        payout_handle: form.payout_handle.trim(),
        notes: form.notes,
        referral_code: (() => { try { return localStorage.getItem("gc_ref") || null; } catch { return null; } })(),
      };
      const r = await api.post("/submissions", payload);
      const data = r.data;
      try {
        localStorage.setItem("gc_customer", JSON.stringify({ email: form.email, name: form.full_name }));
      } catch { /* localStorage unavailable — non-critical */ }
      toast.success(`Submission ${data.reference} received.`);
      nav(
        `/submitted?ref=${encodeURIComponent(data.reference)}&email=${encodeURIComponent(form.email)}&payout=${data.estimated_payout}${data.welcome_bonus_eligible ? "&bonus=1" : ""}`
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedMethod = PAYOUT_METHODS.find((m) => m.id === form.payout_method);

  return (
    <>
      <SEO
        title="Sell Your Gift Card"
        description={`Fill out the form and get paid via PayPal, Zelle, Cash App, Bitcoin or Chime — typically within 24 hours of approval. Minimum $${cfg.min_per_card || 50}/card.`}
        path="/submit"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "Sell a card", path: "/submit" }]}
      />

      <section className="search-home-hero-section" data-testid="submit-hero" style={{ paddingBottom: 30 }}>
        <div className="main-container w-container">
          <div className="search-heading" style={{ textAlign: "center", maxWidth: 760, margin: "0 auto" }}>
            <div style={{ display: "inline-block", padding: "6px 14px", borderRadius: 90, background: "#eaf0ff", color: "#346af8", fontSize: 12, fontWeight: 700, letterSpacing: ".12em", textTransform: "uppercase", marginBottom: 18 }}>
              Sell a card
            </div>
            <h1 className="h1 _700-bold">Sell your gift card <span style={{ color: "#346af8" }}>in minutes</span></h1>
            <p className="main-body-text _500-medium" style={{ marginTop: 18, color: "#4a4d5f", maxWidth: 620, marginLeft: "auto", marginRight: "auto" }}>
              Three quick steps and you&apos;re done — paid to PayPal, Zelle, Cash App, Bitcoin or Chime after verification.
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-16 md:pb-20" style={{ position: "relative", zIndex: 3 }}>
        <div className="space-y-5">
          {/* ============ FORM ============ */}
          <div className="space-y-5">
            {/* STEP 01 — Your details */}
            <Section step="01" title="Your details" subtitle="How should we contact you?">
              <div className="space-y-4">
                <Field label="Full Name" required error={showErr("full_name") && errors.full_name}>
                  <input
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    onBlur={() => markTouched("full_name")}
                    data-testid="input-full-name"
                    placeholder="John Doe"
                    className="tt-inp"
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Email" required error={showErr("email") && errors.email}>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      onBlur={() => markTouched("email")}
                      data-testid="input-email"
                      placeholder="john@example.com"
                      className="tt-inp"
                    />
                  </Field>
                  <Field label="Phone" required error={showErr("phone") && errors.phone}>
                    <input
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      onBlur={() => markTouched("phone")}
                      data-testid="input-phone"
                      placeholder="+1 (555) 000-0000"
                      className="tt-inp"
                    />
                  </Field>
                </div>
              </div>
            </Section>

            {/* STEP 02 — Gift card details */}
            <Section step="02" title="Gift card details" subtitle={`${form.cards.length} card${form.cards.length > 1 ? "s" : ""} added`}>
              <SafetyAccordion />
              <div className="space-y-5 mt-4">
                {form.cards.map((c, idx) => (
                  <CardBlock
                    key={c._id}
                    card={c}
                    idx={idx}
                    brands={brands}
                    canRemove={form.cards.length > 1}
                    errors={errors}
                    showErr={(field) => showErr(`card-${idx}-${field}`)}
                    markTouched={(field) => markTouched(`card-${idx}-${field}`)}
                    onChange={(patch) => updateCard(idx, patch)}
                    onRemove={() => removeCard(idx)}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={addCard}
                data-testid="btn-add-card"
                className="mt-5 w-full h-12 rounded-xl border-2 border-dashed border-[#346af8]/40 text-[#346af8] font-semibold hover:bg-[#f4f6ff] hover:border-[#346af8] transition-all"
              >
                + Add Another Card
              </button>
            </Section>

            {/* STEP 03 — Payment method */}
            <Section step="03" title="Payment method" subtitle="How do you want to get paid?">
              <div className="text-xs font-bold text-[#5b6270] mb-2 uppercase tracking-wider">
                Method <span className="text-rose-600">*</span>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-5">
                {PAYOUT_METHODS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setForm({ ...form, payout_method: m.id, payout_handle: "" })}
                    data-testid={`payout-${m.id}`}
                    className={`tt-pill ${form.payout_method === m.id ? "on" : ""}`}
                    style={{ height: 44, fontSize: 13 }}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
              <Field label={selectedMethod.label} required error={showErr("payout_handle") && errors.payout_handle} hint={selectedMethod.hint}>
                <input
                  value={form.payout_handle}
                  onChange={(e) => setForm({ ...form, payout_handle: e.target.value })}
                  onBlur={() => markTouched("payout_handle")}
                  data-testid="input-payout-handle"
                  placeholder={selectedMethod.placeholder}
                  className="tt-inp"
                />
              </Field>
              <Field label="Notes (optional)" className="mt-4">
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="input-notes"
                  rows={3}
                  placeholder="Anything else we should know?"
                  className="tt-inp"
                  style={{ height: "auto", padding: "12px 16px", resize: "none" }}
                />
              </Field>
            </Section>
          </div>

          {/* ============ SUBMIT CTA (no price, no summary) ============ */}
          <div className="tt-panel" data-testid="submit-cta-panel">
            <div className="tt-panel-inner">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {["Payout typically within 24 hours", "Reviewed by a real person", "Estimate shown before submission", "Track live with your SPARK reference"].map((t) => (
                  <div className="tt-check-row" key={t}>
                    <span className="tt-check-dot"><Check className="w-3 h-3" /></span>
                    {t}
                  </div>
                ))}
              </div>

              <p className="text-[11px] leading-relaxed mb-4" style={{ color: "#848da8" }} data-testid="submit-age-disclosure">
                By tapping <strong style={{ color: "#c8cbe0" }}>Submit</strong>, you confirm you are <strong style={{ color: "#c8cbe0" }}>{BRAND.minimumAgeYears}+ years old</strong>, that the card is yours and was acquired legally, and that you agree to our{" "}
                <Link to="/legal/terms" className="underline" style={{ color: "#8fadff" }}>Terms</Link>,{" "}
                <Link to="/legal/privacy" className="underline" style={{ color: "#8fadff" }}>Privacy Policy</Link>, and{" "}
                <Link to="/legal/refund" className="underline" style={{ color: "#8fadff" }}>Refund Policy</Link>. Service available in the {BRAND.operatingRegion.toLowerCase()}.
              </p>

              <button type="button" disabled={submitting} onClick={submit} data-testid="submit-button" className="tt-panel-cta">
                {submitting ? <Spinner /> : (<><Check className="w-5 h-5" /> Submit my cards</>)}
              </button>
              <div className="mt-4 flex items-center justify-center gap-2 text-xs" style={{ color: "#848da8" }}>
                <Lock className="w-3.5 h-3.5" />
                <span>TLS-encrypted · Minimum ${cfg.min_per_card || 50}/card · Human review</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

/* ============================== SECTION SHELL ============================== */
function Section({ step, title, subtitle, children }) {
  return (
    <div className="tt-card p-6 md:p-8">
      <div className="tt-step-eyebrow">Step {step}</div>
      <div className="mt-3 mb-6">
        <div className="font-bold text-[#0b0d17] leading-tight" style={{ fontFamily: "Satoshi, sans-serif", fontSize: 24, letterSpacing: "-0.02em" }}>
          {title}
        </div>
        <div className="text-sm text-[#848991] mt-1">{subtitle}</div>
      </div>
      {children}
    </div>
  );
}

/* ============================== CARD BLOCK ============================== */
function CardBlock({ card, idx, brands, canRemove, errors, showErr, markTouched, onChange, onRemove }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: EASE }}
      className="border border-[#e4e8f7] rounded-xl p-4 md:p-5 bg-[#f4f6ff]/60"
      data-testid={`card-block-${idx}`}
    >
      {canRemove && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold uppercase tracking-wider text-[#848991]">Card #{idx + 1}</span>
          <button type="button" onClick={onRemove} data-testid={`remove-card-${idx}`} className="p-1.5 text-[#848991] hover:text-rose-600" aria-label="Remove card">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Card Brand" required error={showErr("brand") && errors[`card-${idx}-brand`]} className="sm:col-span-2">
          <BrandInput
            brands={brands}
            value={card.brand_query}
            brandId={card.brand_id}
            onChange={(query, brandId) => {
              onChange({ brand_query: query, brand_id: brandId });
              if (brandId) markTouched("brand");
            }}
            onBlur={() => markTouched("brand")}
            testId={`card-${idx}-brand`}
          />
        </Field>
        <Field label="Value ($)" required error={showErr("balance") && errors[`card-${idx}-balance`]}>
          <input
            type="number"
            value={card.balance}
            onChange={(e) => onChange({ balance: e.target.value })}
            onBlur={() => markTouched("balance")}
            placeholder="$0.00"
            data-testid={`card-${idx}-balance`}
            className="tt-inp"
            min={1}
            step="0.01"
          />
        </Field>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 mt-4">
        <Field label="Card Number / Code">
          <input
            value={card.card_number}
            onChange={(e) => onChange({ card_number: e.target.value })}
            placeholder="Enter card number"
            data-testid={`card-${idx}-number`}
            className="tt-inp"
            autoComplete="off"
          />
        </Field>
        <Field label="PIN / CVV">
          <input
            value={card.pin}
            onChange={(e) => onChange({ pin: e.target.value })}
            placeholder="Enter PIN"
            data-testid={`card-${idx}-pin`}
            className="tt-inp"
            autoComplete="off"
          />
        </Field>
        <Field label={<>Expiry Date <span className="text-[#848991] font-normal">(Visa / MC)</span></>}>
          <input
            value={card.expiry_date}
            onChange={(e) => onChange({ expiry_date: e.target.value })}
            placeholder="MM/YY"
            data-testid={`card-${idx}-expiry`}
            className="tt-inp"
            maxLength={5}
          />
        </Field>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <SegToggle
          label="Type"
          value={card.card_type}
          onChange={(v) => onChange({ card_type: v })}
          options={[{ id: "physical", label: "Physical" }, { id: "digital", label: "Digital" }]}
          testId={`card-${idx}-type`}
        />
        <SegToggle
          label="Condition"
          value={card.condition}
          onChange={(v) => onChange({ condition: v })}
          options={[{ id: "unused", label: "Unused" }, { id: "partial", label: "Partial" }]}
          testId={`card-${idx}-condition`}
        />
        <SegToggle
          label="Receipt?"
          value={card.has_receipt ? "yes" : "no"}
          onChange={(v) => onChange({ has_receipt: v === "yes" })}
          options={[{ id: "yes", label: "Yes" }, { id: "no", label: "No" }]}
          testId={`card-${idx}-receipt`}
        />
      </div>

      <div className="mt-5">
        <div className="text-xs font-bold text-[#5b6270] mb-2 uppercase tracking-wider">
          Card Images <span className="text-[#848991] font-normal normal-case">(optional)</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <ImageDrop label="Front" icon={ImageIcon} value={card.image_front} onChange={(p) => onChange({ image_front: p })} testId={`card-${idx}-img-front`} />
          <ImageDrop label="Back" icon={ImageIcon} value={card.image_back} onChange={(p) => onChange({ image_back: p })} testId={`card-${idx}-img-back`} />
          <ImageDrop label="Receipt" icon={Receipt} value={card.image_receipt} onChange={(p) => onChange({ image_receipt: p })} testId={`card-${idx}-img-receipt`} />
        </div>
        {card.has_receipt && !card.image_receipt && (
          <div className="mt-2 text-xs text-amber-700 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" />
            Receipt-tier rate requires a receipt image.
          </div>
        )}
      </div>
    </motion.div>
  );
}

/* ============================== BRAND INPUT (plain) ============================== */
function BrandInput({ brands, value, brandId, onChange, onBlur, testId }) {
  const resolveBrandId = (query) => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return "";
    const exact = brands.find((b) => b.name.toLowerCase() === q);
    return exact ? exact.id : "";
  };

  return (
    <input
      value={value}
      onChange={(e) => {
        const q = e.target.value;
        onChange(q, resolveBrandId(q));
      }}
      onBlur={onBlur}
      placeholder="e.g., Amazon, Target, Walmart…"
      className="tt-inp"
      data-testid={testId}
      autoComplete="off"
      data-brand-id={brandId || ""}
    />
  );
}

/* ============================== SEGMENT TOGGLE ============================== */
function SegToggle({ label, value, onChange, options, testId }) {
  return (
    <div>
      <div className="text-xs font-bold text-[#5b6270] mb-2 uppercase tracking-wider">{label}</div>
      <div className="inline-flex w-full p-1 rounded-lg bg-white border border-[#e4e8f7]">
        {options.map((o) => (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            data-testid={`${testId}-${o.id}`}
            className={`flex-1 h-9 rounded-md text-sm font-semibold transition-all ${
              value === o.id ? "bg-[#346af8] text-white" : "bg-transparent text-[#5b6270] hover:text-[#0b0d17]"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ============================== IMAGE DROP ============================== */
// Allowed types: JPG/PNG/WEBP. iPhone HEIC/HEIF is converted to JPEG in-browser.
function ImageDrop({ label, icon: Icon, value, onChange, testId }) {
  const inputRef = useRef(null);
  const reactId = useId();
  const inputId = `img-drop-${reactId}`;
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error(`${label}: File too large (max 25MB)`);
      return;
    }
    const allowedExts = ["jpg", "jpeg", "png", "webp"];
    let ext = (file.name || "").split(".").pop()?.toLowerCase() || "";

    const isHeic =
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      ext === "heic" ||
      ext === "heif";
    if (isHeic) {
      setUploading(true);
      try {
        const heic2any = (await import("heic2any")).default;
        let blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
        blob = Array.isArray(blob) ? blob[0] : blob;
        if (blob.size > 8 * 1024 * 1024) {
          let retry = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.6 });
          retry = Array.isArray(retry) ? retry[0] : retry;
          blob = retry;
        }
        file = new File([blob], (file.name || "photo").replace(/\.(heic|heif)$/i, "") + ".jpg", { type: "image/jpeg" });
        ext = "jpg";
      } catch (err) {
        setUploading(false);
        toast.error(`${label}: Couldn't convert this iPhone photo. Please retake as JPG (Settings → Camera → Formats → Most Compatible).`);
        return;
      }
    }

    if (file.size > 8 * 1024 * 1024) {
      setUploading(false);
      toast.error(`${label}: Image too large after processing (max 8MB). Try a smaller photo.`);
      return;
    }

    const typeOk =
      ["image/jpeg", "image/png", "image/webp"].includes(file.type) ||
      (file.type === "" && allowedExts.includes(ext));
    if (!typeOk) {
      toast.error(`${label}: Use JPG, PNG or WEBP`);
      setUploading(false);
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/gift-card", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.path);
    } catch (e) {
      toast.error(`${label}: ${e?.response?.data?.detail || "upload failed"}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <label
      htmlFor={inputId}
      data-testid={testId}
      className={`group relative aspect-[4/3] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-1.5 transition-all cursor-pointer ${
        value ? "border-[#346af8]/60 bg-[#f4f6ff]" : "border-[#e4e8f7] bg-white hover:border-[#346af8]/40 hover:bg-[#f4f6ff]/60"
      }`}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
        className="sr-only"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
      {uploading ? (
        <Spinner small />
      ) : value ? (
        <>
          <Check className="w-5 h-5 text-[#346af8]" />
          <span className="text-[11px] font-semibold text-[#346af8]">{label} uploaded</span>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChange("");
            }}
            aria-label={`Remove ${label} image`}
            className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white border border-[#e4e8f7] text-[#5b6270] flex items-center justify-center hover:text-rose-600"
          >
            <X className="w-3 h-3" />
          </button>
        </>
      ) : (
        <>
          <Icon className="w-5 h-5 text-[#848991] group-hover:text-[#346af8]" />
          <span className="text-[11px] font-semibold text-[#5b6270] group-hover:text-[#0b0d17]">{label}</span>
        </>
      )}
    </label>
  );
}

/* ============================== FIELD WRAPPER ============================== */
function Field({ label, required, error, hint, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-sm font-semibold text-[#0b0d17] mb-2">
        {label}
        {required && <span className="text-[#346af8] ml-1">*</span>}
      </div>
      {children}
      {error ? (
        <div className="mt-2 text-xs text-rose-500/90 flex items-center gap-1.5 font-medium">
          <AlertCircle className="w-3 h-3" />
          {error}
        </div>
      ) : hint ? (
        <div className="mt-2 text-xs text-[#848991]">{hint}</div>
      ) : null}
    </label>
  );
}

function Spinner({ small }) {
  const cls = small ? "w-4 h-4" : "w-5 h-5";
  return <span className={`${cls} inline-block rounded-full border-2 border-current border-t-transparent animate-spin`} />;
}

/* ============================== SAFETY ACCORDION ============================== */
function SafetyAccordion() {
  const [open, setOpen] = useState(false);
  const points = [
    { Icon: FileCheck, t: "Reviewed by a person", d: "After basic image and duplicate checks, a teammate manually verifies authenticity before any approval — no fully-automated payouts." },
    { Icon: KeyRound, t: "Numbers stay masked", d: "Once you submit, the card number is masked in the dashboard. Only the reviewer working your ticket can briefly unmask it — every reveal is logged." },
    { Icon: Lock, t: "Purged after payout", d: "Card numbers, PINs, and images are encrypted at rest and purged automatically once your payout settles. Nothing lingers." },
  ];
  return (
    <div data-testid="safety-accordion" className="border border-[#e4e8f7] rounded-xl bg-white overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="safety-accordion-toggle" className="w-full flex items-center gap-3 px-4 py-3 text-left">
        <span className="inline-flex w-9 h-9 rounded-lg bg-[#f4f6ff] text-[#346af8] items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-4 h-4" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#346af8] font-bold">Why this is safe</div>
          <div className="text-sm font-semibold text-[#0b0d17] mt-0.5">How we protect your card details</div>
        </div>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="text-[#5b6270]">
          <ChevronDown className="w-4 h-4" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 space-y-2.5 border-t border-[#e4e8f7]">
              {points.map((p) => (
                <div key={p.t} className="flex items-start gap-3 pt-3">
                  <span className="w-8 h-8 rounded-lg bg-[#f4f6ff] text-[#346af8] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <p.Icon className="w-4 h-4" />
                  </span>
                  <div>
                    <div className="text-sm font-semibold text-[#0b0d17]">{p.t}</div>
                    <div className="text-xs text-[#5b6270] leading-relaxed mt-0.5">{p.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
